import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Award, Flame, Sparkles, MapPin, Calculator, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtMXN, pricePerKg } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import ParrilladaCalculator from "@/components/ParrilladaCalculator";
import SafeImage from "@/components/SafeImage";
import { useSEO, ORGANIZATION_LD } from "@/lib/seo";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1625604086988-6e41981275fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const DESERT_IMAGE =
  "https://images.unsplash.com/photo-1654330351968-688120047293?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const GRILL_IMAGE =
  "https://images.pexels.com/photos/37179548/pexels-photo-37179548.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export default function HomePage() {
  // Hydrate from sessionStorage cache so revisits paint instantly,
  // even if the API has a transient blip.
  const [packages, setPackages] = useState(() => {
    try {
      const cached = sessionStorage.getItem("arre_home_packages");
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [featuredProducts, setFeaturedProducts] = useState(() => {
    try {
      const cached = sessionStorage.getItem("arre_home_products");
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  const [loadingPackages, setLoadingPackages] = useState(() => {
    try { return !sessionStorage.getItem("arre_home_packages"); } catch { return true; }
  });
  const [loadingProducts, setLoadingProducts] = useState(() => {
    try { return !sessionStorage.getItem("arre_home_products"); } catch { return true; }
  });
  const [packagesError, setPackagesError] = useState(false);
  const [productsError, setProductsError] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const { addItem } = useCart();

  useSEO({
    title: null, // use default brand title
    description: "Boutique de carnes 100% sonorense. Tomahawk, Picaña, Rib Eye, paquetes y parrilladas a domicilio. Envío mismo día en CDMX y EDOMEX.",
    jsonLd: ORGANIZATION_LD,
  });

  const loadWithRetry = async (path, label) => {
    const delays = [0, 700, 2000];
    for (let attempt = 0; attempt < delays.length; attempt++) {
      if (delays[attempt] > 0) await new Promise(r => setTimeout(r, delays[attempt]));
      try {
        const r = await api.get(path, { timeout: 15000 });
        if (Array.isArray(r.data)) return r.data;
        return [];
      } catch (e) {
        console.warn(`[${label}] attempt ${attempt + 1}/${delays.length} failed:`, e.message);
        if (attempt === delays.length - 1) {
          console.error(`[${label}] all retries exhausted`, e);
          return null;
        }
      }
    }
  };

  const loadPackages = async () => {
    setLoadingPackages(true);
    setPackagesError(false);
    const data = await loadWithRetry("/packages", "packages");
    if (data === null) {
      // If we already have cached data, keep showing it instead of error
      if (packages.length === 0) setPackagesError(true);
    } else {
      setPackages(data);
      try { sessionStorage.setItem("arre_home_packages", JSON.stringify(data)); } catch {}
    }
    setLoadingPackages(false);
  };

  const loadProducts = async () => {
    setLoadingProducts(true);
    setProductsError(false);
    const data = await loadWithRetry("/products?featured=true", "products");
    if (data === null) {
      if (featuredProducts.length === 0) setProductsError(true);
    } else {
      setFeaturedProducts(data);
      try { sessionStorage.setItem("arre_home_products", JSON.stringify(data)); } catch {}
    }
    setLoadingProducts(false);
  };

  useEffect(() => {
    loadPackages();
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const heroPackage = packages.find((p) => p.slug === "arre-sonora") || packages[0];

  return (
    <div data-testid="home-page">
      <ParrilladaCalculator open={calcOpen} onClose={() => setCalcOpen(false)} />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden grain">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url('${HERO_IMAGE}')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-arre-black via-arre-black/70 to-arre-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-arre-black/80 via-transparent to-transparent" />

        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 pb-20 pt-40 w-full">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-arre-red/60 bg-arre-red/10 text-arre-red text-[10px] tracking-[0.35em] uppercase mb-6">
              <Flame className="w-3 h-3" />
              100% Carne Sonorense
            </div>
            <h1 className="font-display text-6xl sm:text-8xl lg:text-[9rem] leading-[0.85] tracking-wider uppercase text-white text-glow-red">
              Qué onda,<br />
              <span className="text-arre-red">compare.</span>
            </h1>
            <p className="font-serif-display italic text-2xl md:text-3xl text-white/90 mt-6 max-w-xl leading-snug">
              La mejor carne de México, directo del rancho sonorense a tu parrilla.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link
                to="/paquetes"
                className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-xl tracking-widest uppercase px-10 py-5 inline-flex items-center gap-3"
                data-testid="hero-cta-paquetes"
              >
                Ver paquetes <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/catalogo/cortes"
                className="btn-arre border border-white/30 hover:border-white text-white font-display text-xl tracking-widest uppercase px-10 py-5"
                data-testid="hero-cta-cortes"
              >
                Ver cortes
              </Link>
            </div>
          </div>
        </div>

        {/* Trust row */}
        <div className="relative z-10 w-full border-t border-white/10 backdrop-blur-xl bg-arre-black/50">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4">
            {[
              { icon: Award, label: "100% Sonorense", sub: "Calidad garantizada" },
              { icon: Truck, label: "Envío Mismo Día", sub: "CDMX / EDOMEX" },
              { icon: MapPin, label: "Cobertura Nacional", sub: "Toda la república" },
              { icon: Sparkles, label: "Corte Boutique", sub: "Selección premium" },
            ].map((t, i) => (
              <div key={i} className="flex items-center gap-4 py-6 px-4 border-r border-white/10 last:border-r-0">
                <t.icon className="w-6 h-6 text-arre-red flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <div className="font-display text-lg tracking-wider uppercase text-white leading-none">{t.label}</div>
                  <div className="text-xs text-white/50 mt-1">{t.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BYOP + CALCULATOR STRIP */}
      <section className="bg-arre-black py-16 md:py-24 border-b border-arre-border" data-testid="byop-calc-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* BYOP */}
            <Link
              to="/arma-tu-paquete"
              className="group relative overflow-hidden bg-arre-surface border border-arre-border hover:border-arre-red transition-colors p-8 md:p-12 min-h-[360px] grain flex flex-col justify-between"
              data-testid="home-byop-cta"
            >
              <div
                className="absolute inset-0 bg-cover bg-center opacity-10 group-hover:opacity-20 transition"
                style={{ backgroundImage: "url('https://images.unsplash.com/photo-1558030006-450675393462?auto=format&fit=crop&w=1200&q=85')" }}
              />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-arre-red/15 border border-arre-red/40 text-arre-red text-[10px] tracking-[0.3em] uppercase mb-4">
                  <Wand2 className="w-3 h-3" />
                  15% OFF desde $1,200
                </div>
                <h3 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-[0.85]">
                  Arma tu<br />
                  <span className="text-arre-red italic font-serif-display normal-case tracking-tight">paquete</span>
                </h3>
                <p className="font-serif-display italic text-lg text-white/75 mt-4 max-w-md">
                  Elige tus cortes, quesos y complementos. Armamos el combo a tu medida y te regalamos 15% off, compare.
                </p>
              </div>
              <div className="relative inline-flex items-center gap-2 text-arre-red font-display text-lg tracking-widest uppercase">
                Arrancar → <ArrowRight className="w-5 h-5 transition group-hover:translate-x-1" />
              </div>
            </Link>

            {/* CALCULATOR */}
            <button
              onClick={() => setCalcOpen(true)}
              className="group relative overflow-hidden bg-arre-surface border border-arre-border hover:border-arre-red transition-colors p-8 md:p-12 min-h-[360px] grain flex flex-col justify-between text-left"
              data-testid="home-calc-cta"
            >
              <div
                className="absolute inset-0 bg-cover bg-center opacity-10 group-hover:opacity-20 transition"
                style={{ backgroundImage: `url('${GRILL_IMAGE}')` }}
              />
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 border border-white/20 text-white text-[10px] tracking-[0.3em] uppercase mb-4">
                  <Calculator className="w-3 h-3" />
                  Calculadora inteligente
                </div>
                <h3 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-[0.85]">
                  ¿Cuántos<br />
                  <span className="text-arre-red italic font-serif-display normal-case tracking-tight">compares?</span>
                </h3>
                <p className="font-serif-display italic text-lg text-white/75 mt-4 max-w-md">
                  Dinos cuántos van a comer y te decimos exactamente cuánta carne necesitas + qué paquete te queda perfecto.
                </p>
              </div>
              <div className="relative inline-flex items-center gap-2 text-arre-red font-display text-lg tracking-widest uppercase">
                Calcular → <ArrowRight className="w-5 h-5 transition group-hover:translate-x-1" />
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* PAQUETES HERO SECTION */}
      <section className="py-24 md:py-32 bg-arre-black" data-testid="packages-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div className="max-w-2xl">
              <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Los favoritos del compare</div>
              <h2 className="font-display text-6xl md:text-8xl tracking-wider uppercase text-white leading-none">
                Paquetes<br />
                <span className="text-arre-red italic font-serif-display text-5xl md:text-7xl normal-case tracking-tight">que no fallan</span>
              </h2>
            </div>
            <Link
              to="/paquetes"
              className="font-display text-lg tracking-widest uppercase text-white/70 hover:text-arre-red inline-flex items-center gap-2"
            >
              Ver todos <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Bento grid */}
          {loadingPackages ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="packages-skeleton">
              <div className="lg:col-span-2 lg:row-span-2 aspect-[16/11] lg:aspect-auto bg-arre-surface border border-arre-border animate-pulse min-h-[400px]" />
              <div className="aspect-[4/5] bg-arre-surface border border-arre-border animate-pulse" />
              <div className="aspect-[4/5] bg-arre-surface border border-arre-border animate-pulse" />
            </div>
          ) : packagesError ? (
            <div className="bg-arre-surface border border-arre-border p-10 text-center" data-testid="packages-empty">
              <p className="font-display text-2xl tracking-wider uppercase text-white/70 mb-3">Estamos cargando los paquetes…</p>
              <p className="text-white/50 text-sm mb-5">Si la conexión está lenta, dale un toque al botón.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  onClick={loadPackages}
                  className="btn-arre bg-arre-red text-white font-display text-sm tracking-widest uppercase px-5 py-3"
                  data-testid="reload-packages-btn"
                >
                  Reintentar
                </button>
                <Link
                  to="/paquetes"
                  className="btn-arre border border-arre-border hover:border-arre-red text-white font-display text-sm tracking-widest uppercase px-5 py-3"
                >
                  Ir al catálogo →
                </Link>
              </div>
            </div>
          ) : !heroPackage || packages.length === 0 ? (
            <div className="bg-arre-surface border border-arre-border p-10 text-center">
              <p className="font-display text-xl text-white/60">Próximamente más paquetes</p>
              <Link to="/paquetes" className="text-arre-red text-xs tracking-widest uppercase mt-3 inline-block hover:underline">Ver catálogo →</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* Hero pkg */}
              <Link
                to={`/paquete/${heroPackage.slug}`}
                className="lg:col-span-2 lg:row-span-2 group relative overflow-hidden bg-arre-surface border border-arre-border hover-zoom"
                data-testid={`package-hero-${heroPackage.slug}`}
              >
                <div className="aspect-[16/11] lg:aspect-auto lg:h-full overflow-hidden">
                  <SafeImage src={heroPackage.image} alt={heroPackage.name} fallbackLabel={heroPackage.name} className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-arre-black via-arre-black/40 to-transparent" />
                {heroPackage.badge && (
                  <span className="absolute top-5 left-5 bg-arre-red text-white text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-1.5">
                    {heroPackage.badge}
                  </span>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-10">
                  <div className="text-xs tracking-[0.3em] uppercase text-arre-red mb-2">{heroPackage.tagline}</div>
                  <h3 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-none">{heroPackage.name}</h3>
                  <p className="font-serif-display italic text-lg text-white/80 mt-4 max-w-lg">{heroPackage.description}</p>
                  <div className="flex items-end justify-between mt-6 gap-4">
                    <div>
                      <div className="text-xs tracking-widest uppercase text-white/50">Para {heroPackage.serves}</div>
                      <div className="font-display text-5xl text-white mt-1">{fmtMXN(heroPackage.price)}</div>
                    </div>
                    <div className="btn-arre bg-arre-red text-white font-display text-lg tracking-widest uppercase px-6 py-3 group-hover:bg-arre-red-dark">
                      Ver paquete →
                    </div>
                  </div>
                </div>
              </Link>

              {/* Secondary pkgs */}
              {packages.filter((p) => p.slug !== heroPackage.slug).slice(0, 2).map((p) => (
                <Link
                  key={p.slug}
                  to={`/paquete/${p.slug}`}
                  className="group relative overflow-hidden bg-arre-surface border border-arre-border hover-zoom"
                  data-testid={`package-card-${p.slug}`}
                >
                  <div className="aspect-[4/5] overflow-hidden">
                    <SafeImage src={p.image} alt={p.name} fallbackLabel={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-arre-black via-arre-black/30 to-transparent" />
                  {p.badge && (
                    <span className="absolute top-4 left-4 bg-arre-red text-white text-[9px] tracking-[0.3em] uppercase font-bold px-2.5 py-1">
                      {p.badge}
                    </span>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-arre-red mb-1">{p.tagline}</div>
                    <h3 className="font-display text-3xl tracking-wider uppercase text-white leading-none">{p.name}</h3>
                    <div className="flex items-center justify-between mt-4">
                      <span className="font-display text-2xl text-white">{fmtMXN(p.price)}</span>
                      <ArrowRight className="w-5 h-5 text-arre-red" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* STORY STRIP */}
      <section className="relative py-32 overflow-hidden grain">
        <div className="absolute inset-0 bg-cover bg-center bg-fixed" style={{ backgroundImage: `url('${DESERT_IMAGE}')` }} />
        <div className="absolute inset-0 bg-arre-black/75" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-4">⟡ Nuestra historia</div>
          <h2 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-[0.95] max-w-4xl mx-auto">
            Del rancho<br /><span className="text-arre-red">a tu parrilla.</span>
          </h2>
          <p className="font-serif-display italic text-xl md:text-2xl text-white/85 mt-8 max-w-2xl mx-auto leading-snug">
            En Sonora criamos la mejor carne de México — tierra del ganado bronco, del asado al carbón de mezquite, de la tradición
            norteña. Cada corte de Arre Sonora viene de ahí: de donde la calidad no se discute, compare.
          </p>
        </div>
      </section>

      {/* FEATURED CORTES */}
      <section className="py-24 md:py-32 bg-arre-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Cortes destacados</div>
              <h2 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-none">
                Los reyes<br /><span className="text-arre-red italic font-serif-display normal-case tracking-tight">de la parrilla</span>
              </h2>
            </div>
            <Link to="/catalogo/cortes" className="font-display text-lg tracking-widest uppercase text-white/70 hover:text-arre-red inline-flex items-center gap-2">
              Ver todos <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loadingProducts ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="products-skeleton">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-arre-surface border border-arre-border animate-pulse">
                  <div className="aspect-[4/5]" />
                  <div className="p-4 md:p-5 space-y-2">
                    <div className="h-3 bg-white/5 w-1/3" />
                    <div className="h-6 bg-white/10" />
                    <div className="h-5 bg-white/10 w-1/2 mt-3" />
                  </div>
                </div>
              ))}
            </div>
          ) : productsError ? (
            <div className="bg-arre-surface border border-arre-border p-10 text-center" data-testid="products-empty">
              <p className="font-display text-2xl tracking-wider uppercase text-white/70 mb-3">Estamos cargando los cortes…</p>
              <p className="text-white/50 text-sm mb-5">Si la conexión está lenta, dale un toque al botón.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  onClick={loadProducts}
                  className="btn-arre bg-arre-red text-white font-display text-sm tracking-widest uppercase px-5 py-3"
                  data-testid="reload-products-btn"
                >
                  Reintentar
                </button>
                <Link
                  to="/catalogo/cortes"
                  className="btn-arre border border-arre-border hover:border-arre-red text-white font-display text-sm tracking-widest uppercase px-5 py-3"
                >
                  Ver todo el catálogo →
                </Link>
              </div>
            </div>
          ) : featuredProducts.length === 0 ? (
            <div className="bg-arre-surface border border-arre-border p-10 text-center">
              <p className="font-display text-xl text-white/60">Próximamente más cortes destacados</p>
              <Link to="/catalogo/cortes" className="text-arre-red text-xs tracking-widest uppercase mt-3 inline-block hover:underline">Ver todos →</Link>
            </div>
          ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {featuredProducts.map((p) => {
              const hasMulti = (p.variants?.length || 0) > 1;
              const startVariant = p.variants?.[0];
              const startPrice = startVariant?.price ?? p.price;
              const startWeight = startVariant?.weight_g ?? p.weight_g;
              const perKg = pricePerKg({ price: startPrice, weight_g: startWeight });
              return (
                <Link
                  to={`/producto/${p.slug}`}
                  key={p.id}
                  className="group block bg-arre-surface border border-arre-border hover:border-arre-red transition-colors hover-zoom"
                  data-testid={`featured-product-${p.slug}`}
                >
                  <div className="aspect-[4/5] overflow-hidden bg-arre-black">
                    <SafeImage src={p.image} alt={p.name} fallbackLabel={p.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100" />
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-white/40 mb-1">
                      {hasMulti ? "350 g · 1 kg" : p.unit}
                    </div>
                    <h3 className="font-display text-2xl tracking-wider uppercase text-white leading-none">{p.name}</h3>
                    <div className="flex items-end justify-between mt-4 gap-2">
                      <div>
                        {hasMulti && <div className="text-[9px] tracking-widest uppercase text-white/40">desde</div>}
                        <span className="font-display text-xl text-white">{fmtMXN(startPrice)}</span>
                        {perKg && (
                          <div className="text-[10px] tracking-widest uppercase text-white/50 mt-0.5" data-testid={`featured-price-per-kg-${p.slug}`}>
                            {perKg} / kg
                          </div>
                        )}
                      </div>
                      {hasMulti ? (
                        <span className="text-[10px] tracking-[0.25em] uppercase text-arre-red font-bold">
                          Elegir →
                        </span>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            const variant = p.variants?.[0];
                            addItem({
                              type: "product",
                              id: p.id,
                              variant_id: variant?.id || null,
                              name: variant ? `${p.name} — ${variant.label}` : p.name,
                              price: variant?.price ?? p.price,
                              image: p.image,
                              unit: variant?.label || p.unit,
                            });
                          }}
                          className="text-[10px] tracking-[0.25em] uppercase text-arre-red hover:text-white font-bold"
                          data-testid={`add-product-${p.slug}`}
                        >
                          + Agregar
                        </button>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          )}
        </div>
      </section>

      {/* CATEGORIES STRIP */}
      <section className="py-24 bg-arre-surface/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <h2 className="font-display text-4xl md:text-6xl tracking-wider uppercase text-white mb-10">Explora la boutique</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { slug: "cortes", label: "Cortes", img: "https://images.pexels.com/photos/9541962/pexels-photo-9541962.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400" },
              { slug: "paquetes", label: "Paquetes", img: "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?auto=format&fit=crop&w=400&q=85" },
              { slug: "charcuteria", label: "Charcutería", img: "https://images.unsplash.com/photo-1627309366721-b63e8d68e62c?auto=format&fit=crop&w=400&q=85" },
              { slug: "quesos", label: "Quesos", img: "https://images.unsplash.com/photo-1486297678162-eb2a19b0a32d?auto=format&fit=crop&w=400&q=85" },
              { slug: "complementos", label: "Complementos", img: "https://images.unsplash.com/photo-1613514785940-daed07799d9b?auto=format&fit=crop&w=400&q=85" },
              { slug: "extras", label: "Extras", img: "https://images.unsplash.com/photo-1514533212735-5df27d970db0?auto=format&fit=crop&w=400&q=85" },
            ].map((c) => (
              <Link
                key={c.slug}
                to={c.slug === "paquetes" ? "/paquetes" : `/catalogo/${c.slug}`}
                className="relative group aspect-square overflow-hidden bg-arre-black border border-arre-border hover-zoom"
                data-testid={`category-${c.slug}`}
              >
                <img src={c.img} alt={c.label} className="w-full h-full object-cover opacity-60 group-hover:opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-arre-black to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="font-display text-2xl tracking-wider uppercase text-white leading-none">{c.label}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* GRILL CTA */}
      <section className="relative py-32 overflow-hidden grain">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${GRILL_IMAGE}')` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-arre-black via-arre-black/85 to-arre-black/40" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Entrega exprés</div>
            <h2 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-[0.95]">
              ¿Se te antojó<br /><span className="text-arre-red">compare?</span>
            </h2>
            <p className="font-serif-display italic text-xl text-white/85 mt-6">
              Pide antes de las 12 y tu pedido llega HOY MISMO en CDMX y Estado de México. Envío refrigerado nacional.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                to="/paquetes"
                className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-xl tracking-widest uppercase px-10 py-5"
              >
                Pedir ahora →
              </Link>
              <button
                onClick={() => window.dispatchEvent(new Event("open_zip_popup"))}
                className="btn-arre border border-white/30 hover:border-white text-white font-display text-xl tracking-widest uppercase px-10 py-5"
                data-testid="cta-check-zip"
              >
                ¿Tengo cobertura?
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

